/**
 * ArtifactViewPage — the member-gated viewer for one published artifact
 * (/artifact/:id). Cloud port of the self-host dashboard's #/artifact/<id>
 * view (backend/selfhosted/ui/index.html — artifactViewPage).
 *
 * The artifact skill publishes self-contained shareable pages (reports, plans,
 * write-ups); the ONLY read path is the authed, member-gated
 * GET /credits/artifacts/:id → { metadata, content, format, rendered }
 * (src/handlers/artifacts.js — there is deliberately NO unauthenticated serve
 * route). Login is enforced by ProtectedRoute; project MEMBERSHIP is enforced
 * server-side, so a non-member sees the error state, never the content.
 *
 * `rendered` is the full HTML document (markdown already converted, CSP <meta>
 * blocking all network egress injected). We drop it into a SANDBOXED iframe via
 * srcDoc with sandbox="allow-scripts" ONLY — the opaque origin means the
 * untrusted artifact JS can't read the session token, and no allow-same-origin
 * / allow-top-navigation is ever added.
 *
 * THE DESCRIPTION (`metadata.descriptionHtml`) renders BELOW the artifact. It
 * is an agent-authored Markdown note explaining what the reader is looking at —
 * a `.webm` cannot explain itself, and before this the page's entire vocabulary
 * for a recording was `video/webm · 3.8 MB · Download`.
 *
 * ⚠️ IT IS RENDERED SERVER-SIDE AND THAT IS THE SECURITY DECISION, NOT AN
 * OPTIMISATION. A description is written by an agent whose context holds
 * third-party text (PR diffs, ticket bodies, scraped pages), so it is untrusted
 * input. The backend's `renderMarkdown` (utils/markdown-lite.js) HTML-escapes
 * the source FIRST and only then applies its transforms, so no author-supplied
 * tag survives and link hrefs are scheme-checked. This page therefore parses NO
 * Markdown and adds NO renderer: doing it here would be a SECOND renderer (the
 * self-host dashboard shares zero code with this file), i.e. two XSS surfaces
 * that can drift apart. Unlike `rendered`, this fragment goes in the page rather
 * than the sandboxed iframe, which is exactly why the escaping must be — and is
 * — unconditional at the source.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate, Link } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import { accountApi } from '../../services/api';
import './ArtifactViewPage.css';

// Same shape the backend enforces (ARTIFACT_ID_RE) — reject junk before
// spending the round-trip.
const ARTIFACT_ID_RE = /^[a-f0-9-]{36}$/;

const EMPTY_DOC = '<!doctype html><meta charset="utf-8"><p>Empty artifact.</p>';

// Inline text artifacts (the TEXT_INLINE_TYPES subset the backend serves with
// a decoded `content` string when ≤256 KB) get a read-only source view instead
// of the iframe. Anything else — docs (text/html|text/markdown contentType,
// which render), binary, over-cap text — is untouched.
const TEXT_VIEW_TYPES = ['text/plain', 'text/csv', 'application/json'];

// File extension → [human label, highlight profile key | null]. The profile
// keys map to ARTIFACT_HL below; an unknown extension stays a readable plain
// block (never a guess).
// MIRRORED, NOT SHARED: the self-host dashboard's twin is ARTIFACT_LANGS /
// artifactLangInfo in selfhosted/ui/index.html — the two UIs share zero code
// by design and must be changed in pairs.
const ARTIFACT_LANGS = {
  js: ['JavaScript', 'c'], mjs: ['JavaScript', 'c'], cjs: ['JavaScript', 'c'], jsx: ['JavaScript', 'c'],
  ts: ['TypeScript', 'c'], tsx: ['TypeScript', 'c'],
  py: ['Python', 'hash'], rb: ['Ruby', 'hash'], sh: ['Shell', 'hash'], bash: ['Shell', 'hash'],
  zsh: ['Shell', 'hash'], yaml: ['YAML', 'hash'], yml: ['YAML', 'hash'], toml: ['TOML', 'hash'],
  java: ['Java', 'c'], kt: ['Kotlin', 'c'], swift: ['Swift', 'c'], go: ['Go', 'c'], rs: ['Rust', 'c'],
  c: ['C', 'c'], h: ['C', 'c'], cpp: ['C++', 'c'], cc: ['C++', 'c'], hpp: ['C++', 'c'],
  cs: ['C#', 'c'], php: ['PHP', 'c'], scala: ['Scala', 'c'], dart: ['Dart', 'c'],
  sql: ['SQL', 'sql'], json: ['JSON', 'json'], css: ['CSS', 'c'], scss: ['SCSS', 'c'],
  md: ['Markdown', null], csv: ['CSV', null], txt: ['Text', null], xml: ['XML', null], html: ['HTML', null],
};

// Language for the code view: the title's file extension (agents are told to
// put the filename in `title`), falling back to the contentType. Returns
// { name: human label, hl: highlight profile key | null }.
export const artifactLangInfo = (title, contentType) => {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(String(title || '').trim());
  const hit = m ? ARTIFACT_LANGS[m[1].toLowerCase()] : null;
  if (hit) return { name: hit[0], hl: hit[1] };
  if (contentType === 'application/json') return { name: 'JSON', hl: 'json' };
  if (contentType === 'text/csv') return { name: 'CSV', hl: null };
  return { name: 'Text', hl: null };
};

// Minimal self-contained highlighter — comments, strings, numbers, keywords;
// everything else stays plain. Three comment "profiles" cover the mainstream
// languages (+ 'json'); per-language keyword lists stay deliberately short
// (control flow + declarations), because a missed keyword renders plain —
// never wrong. Output is ONLY strings + <span> React elements: React's
// default escaping is the injection defense for the untrusted model-authored
// source (never dangerouslySetInnerHTML).
// MIRRORED, NOT SHARED: the self-host dashboard's twin is ARTIFACT_HL /
// highlightCodeInto in selfhosted/ui/index.html (DOM-node flavor of the same
// logic) — change one, change the other.
const ARTIFACT_HL = {
  c: {
    re: /(\/\*[\s\S]*?\*\/|\/\/[^\n]*)|("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|`(?:[^`\\]|\\.)*`)|\b(\d[\d_]*(?:\.\d+)?(?:e[+-]?\d+)?)\b|\b([A-Za-z_$][\w$]*)\b/g,
    kw: 'abstract as async await break case catch class const continue def default defer do else enum export extends final finally fn for func function go if impl implements import in interface internal let loop match mod module mut new nil none null package private protected pub public return self static struct super switch this throw trait try type typeof use val var void while with yield true false undefined',
  },
  hash: {
    re: /(#[^\n]*)|("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|\b(\d[\d_]*(?:\.\d+)?(?:e[+-]?\d+)?)\b|\b([A-Za-z_][\w?!]*)\b/g,
    kw: 'and as assert begin break case class def defined del do elif else end ensure except exit false finally for from global if import in is lambda module next nil none not or pass raise require rescue retry return self then true unless until when while with yield',
  },
  sql: {
    re: /(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^'']|'')*'|"(?:[^"\\\n]|\\.)*")|\b(\d[\d_]*(?:\.\d+)?)\b|\b([A-Za-z_][\w]*)\b/g,
    kw: 'select from where insert into update delete set values create table index view drop alter join left right inner outer on as and or not null primary key foreign references group by order having limit offset union distinct case when then else end between like in exists',
  },
  json: {
    re: /()("(?:[^"\\]|\\.)*")|(-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b)|\b(true|false|null)\b/g,
    kw: 'true false null',
  },
};

// Lazily-built keyword Sets, one per profile (the space-joined kw string is
// the mirrored source of truth; the Set is just the lookup form).
const kwSetCache = {};
const kwSetFor = (hlKey) => {
  if (!kwSetCache[hlKey]) kwSetCache[hlKey] = new Set(ARTIFACT_HL[hlKey].kw.split(' '));
  return kwSetCache[hlKey];
};

// React flavor of the self-host highlightCodeInto: returns an array of plain
// strings and keyed <span> elements with token classNames (tk-c comment /
// tk-s string / tk-n number / tk-k keyword). Same guards as the reference:
// zero-width-match lastIndex bump + a 200k-iteration hard cap (a pathological
// body renders partially highlighted rather than hanging the tab).
const highlightCode = (body, hlKey) => {
  const prof = hlKey && ARTIFACT_HL[hlKey];
  if (!prof) return body;
  const kw = kwSetFor(hlKey);
  const re = new RegExp(prof.re.source, 'gi');
  const out = [];
  let last = 0;
  let m;
  let guard = 0;
  while ((m = re.exec(body)) && guard++ < 200000) {
    if (m.index > last) out.push(body.slice(last, m.index));
    const cls = m[1] ? 'tk-c' : m[2] ? 'tk-s' : m[3] ? 'tk-n'
      : (m[4] && kw.has(m[4].toLowerCase()) ? 'tk-k' : null);
    if (cls) {
      out.push(<span key={out.length} className={cls}>{m[0]}</span>);
    } else {
      out.push(m[0]);
    }
    last = re.lastIndex;
    if (m.index === re.lastIndex) re.lastIndex += 1; // zero-width safety
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
};

/**
 * Read-only source view for an inline text artifact. Content is rendered as
 * React children ONLY (default escaping is the injection defense — never
 * dangerouslySetInnerHTML here). JSON pretty-prints when it parses; otherwise
 * shown raw with a note saying so.
 *
 * MIRRORED, NOT SHARED: the self-host dashboard's twin is buildTextArtifactView
 * (with highlightCodeInto / ARTIFACT_LANGS) in selfhosted/ui/index.html — the
 * two UIs share zero code by design and must be changed in pairs.
 */
const ArtifactCodeView = ({ meta, content, download }) => {
  const [copied, setCopied] = useState(false);
  const { text, note } = useMemo(() => {
    if (meta?.contentType === 'application/json') {
      try {
        return { text: JSON.stringify(JSON.parse(content), null, 2), note: 'pretty-printed' };
      } catch (e) {
        return { text: content, note: 'not valid JSON — showing raw' };
      }
    }
    return { text: content, note: null };
  }, [meta, content]);
  const langInfo = useMemo(
    () => artifactLangInfo(meta?.title, meta?.contentType),
    [meta],
  );
  // application/json always takes the json profile (a pretty-printed body is
  // JSON whatever the title's extension says) — same rule as the reference.
  const highlighted = useMemo(
    () => highlightCode(text, meta?.contentType === 'application/json' ? 'json' : langInfo.hl),
    [text, meta, langInfo],
  );
  const gutter = useMemo(
    () => text.split('\n').map((_, i) => i + 1).join('\n'),
    [text],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable (permissions / insecure context) */ }
  };

  return (
    <div className="artifact-code-card">
      <div className="artifact-code-toolbar">
        <span className="artifact-code-lang">{langInfo.name}</span>
        {note && <span className="artifact-code-note">{note}</span>}
        <span className="artifact-code-spacer" />
        <button type="button" className="artifact-code-copy" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        {download?.url && (
          <a
            className="artifact-code-download"
            href={download.url}
            download={meta?.title || true}
            target="_blank"
            rel="noreferrer"
          >
            Download
          </a>
        )}
      </div>
      <div className="artifact-code-scroll">
        <pre className="artifact-code-gutter" aria-hidden="true">{gutter}</pre>
        <pre className="artifact-code-body"><code>{highlighted}</code></pre>
      </div>
    </div>
  );
};

/** Human byte size — MIRRORED from the self-host dashboard's fmtBytes. */
function formatBytes(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The media card for a BINARY artifact — video / audio / image / anything else.
 *
 * This branch did not exist: a `video/webm` fell through to the iframe, whose
 * `srcDoc` is `rendered`, which the backend deliberately leaves null for bytes
 * — so every recording on cloud rendered the literal string "Empty artifact."
 * The self-host dashboard has had the media card since it shipped
 * (selfhosted/ui/index.html renderArtifactFrame); this is its cloud twin.
 * MIRRORED, NOT SHARED — the two UIs must be changed in pairs.
 */
const ArtifactMediaView = ({ meta, download }) => {
  const ct = meta?.contentType || '';
  const size = formatBytes(meta?.bytes);
  // NO `contentType · size · Download` strip under a playable/visible medium.
  // It was the whole page back when this branch didn't exist; now the video
  // plays and the image shows, and restating the MIME type and the byte count
  // is the same noise the artifact title and description were added to replace
  // (founder, twice: "remove that"). The medium keeps its own affordances — a
  // <video>/<audio> carries the browser's download control, an <img> its
  // context menu. The opaque-binary branch below KEEPS the link, because there
  // nothing renders and the download is the only way to reach the bytes.
  if (ct.startsWith('video/')) {
    return (
      <div className="artifact-media-card">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video className="artifact-media-el" controls playsInline src={download.url} />
      </div>
    );
  }
  if (ct.startsWith('audio/')) {
    return (
      <div className="artifact-media-card">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio className="artifact-media-audio" controls src={download.url} />
      </div>
    );
  }
  if (ct.startsWith('image/')) {
    return (
      <div className="artifact-media-card">
        <img className="artifact-media-img" alt={meta?.title || 'Artifact image'} src={download.url} />
      </div>
    );
  }
  return (
    <div className="artifact-media-card artifact-media-file">
      <div className="artifact-media-file-title">{meta?.title || 'Binary artifact'}</div>
      <div className="artifact-media-hint">{ct}{size ? ` · ${size}` : ''}</div>
      <a className="artifact-media-download" href={download.url} download>Download</a>
    </div>
  );
};

/**
 * The agent's note about this artifact, under it.
 *
 * `descriptionHtml` is produced by the backend's escape-first Markdown renderer
 * (see the file header) — this component inserts it and parses nothing. Renders
 * NOTHING at all when there is no description, so every artifact published
 * before the field existed looks exactly as it does today: no empty box, no
 * heading with nothing under it.
 */
const ArtifactDescription = ({ html }) => {
  if (!html) return null;
  return (
    <section className="artifact-description" aria-label="About this artifact">
      <h2 className="artifact-description-head">About this artifact</h2>
      {/* eslint-disable-next-line react/no-danger */}
      <div className="artifact-description-body" dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  );
};

const ArtifactViewPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ARTIFACT_ID_RE.test(id || '')) {
      setLoading(false);
      setError('Invalid artifact link.');
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    // /credits/* rides the account API client (same base the credit ledger
    // uses — the CreditsApi RestApi is mapped under the main custom domain
    // with the '/credits' basePath stripped before the handler).
    accountApi
      .get(`/credits/artifacts/${encodeURIComponent(id)}`)
      .then((res) => { if (!cancelled) setData(res || null); })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'You may not have access to this artifact.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const meta = data?.metadata || null;
  const title = meta?.title || 'Artifact';
  // Text view only when the read actually returned the source (over-cap text
  // omits `content` and keeps today's binary behavior).
  const isTextView = !loading && !error
    && typeof data?.content === 'string'
    && TEXT_VIEW_TYPES.includes(meta?.contentType);
  // A binary artifact the backend presigned a download for. Checked AFTER the
  // text view so a small text/plain upload keeps its code viewer (the backend
  // marks those `binary` too and serves both `content` and `download`).
  const isMediaView = !loading && !error && !isTextView
    && meta?.binary === true && !!data?.download?.url;

  let body;
  if (loading) {
    body = (
      <div className="artifact-view-loading" role="status">
        <IonSpinner name="dots" />
        <span>Loading artifact…</span>
      </div>
    );
  } else if (error) {
    body = (
      <div className="artifact-view-error" role="alert">
        <h2>Can’t view this artifact</h2>
        <p>{error}</p>
        <Link className="artifact-view-back" to="/projects">Back to projects</Link>
      </div>
    );
  } else if (isTextView) {
    body = <ArtifactCodeView meta={meta} content={data.content} download={data.download} />;
  } else if (isMediaView) {
    body = <ArtifactMediaView meta={meta} download={data.download} />;
  } else {
    body = (
      <iframe
        className="artifact-view-frame"
        title={title}
        // Opaque origin: no credentials, no top-nav. The rendered doc also
        // carries a CSP <meta> blocking all network egress (backend-injected).
        sandbox="allow-scripts"
        srcDoc={data?.rendered || EMPTY_DOC}
      />
    );
  }

  return (
    <div className="artifact-view-page">
      <Helmet>
        <title>{title} — Zibby</title>
      </Helmet>
      <header className="artifact-view-head">
        <div className="artifact-view-title-row">
          {meta?.favicon && (
            <span className="artifact-view-favicon" aria-hidden="true">{meta.favicon}</span>
          )}
          <h1 className="artifact-view-title">{title}</h1>
        </div>
        <div className="artifact-view-meta">
          <span>
            {isTextView
              ? 'Read-only source view'
              : 'Shared page · sandboxed viewer (no network, no credentials)'}
          </span>
          {meta?.kind && <span className="artifact-view-meta-sep">·</span>}
          {meta?.kind && <span>{meta.kind}</span>}
          {meta?.updatedAt && <span className="artifact-view-meta-sep">·</span>}
          {meta?.updatedAt && (
            <span>Updated {new Date(meta.updatedAt).toLocaleString()}</span>
          )}
        </div>
      </header>
      {body}
      {/* BELOW the artifact, deliberately: the artifact is the thing, this
          explains it. Renders nothing when there is no description. */}
      {!loading && !error && <ArtifactDescription html={meta?.descriptionHtml} />}
    </div>
  );
};

/**
 * Compat shim for the backend's minted share URLs. The write path advertises
 * `<ZIBBY_PUBLIC_URL>/ui#/artifact/<id>` (the self-host dashboard route shape);
 * on cloud there is no /ui and BrowserRouter ignores the hash, so a bare visit
 * would bounce to /. Registered at /ui, this translates the hash form onto the
 * real /artifact/:id route (and falls through to / for any other /ui visit).
 */
export const UiArtifactRedirect = () => {
  const m = (window.location.hash || '').match(/^#\/artifact\/([a-f0-9-]{36})$/);
  return <Navigate to={m ? `/artifact/${m[1]}` : '/'} replace />;
};

export default ArtifactViewPage;
